import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { prefersReducedMotion } from "../utils/motion";

export const CATEGORIES = [
  "Agriculture",
  "Apparel",
  "Automobiles",
  "Banking",
  "Beverages",
  "Chemicals",
  "Construction",
  "Cosmetics",
  "Currencies",
  "Education",
  "Electronics",
  "Food",
  "Government",
  "Media",
  "Organizations",
  "Petroleum",
  "Pharmaceuticals",
  "Other",
];

// How far the pointer may travel with the button down and still count as a
// click. Matches the ~5px browsers use before they start a native drag.
const DRAG_SLOP = 5;

/**
 * Auto-scrolling, drag-scrollable strip of category chips. Each chip links into
 * the existing /search?category= route.
 */
const CategoryStrip = () => {
  const stripRef = useRef(null);
  const pausedRef = useRef(false);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const startXRef = useRef(0);
  const scrollStartRef = useRef(0);
  const [reduceMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    if (reduceMotion) return;

    let frame;
    const step = () => {
      const el = stripRef.current;
      // Skip work while hidden. A background tab used to burn a frame forever.
      if (el && !pausedRef.current && !document.hidden) {
        el.scrollLeft += 0.4;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [reduceMotion]);

  return (
    <div
      ref={stripRef}
      className="cat-strip"
      onMouseDown={(e) => {
        e.preventDefault();
        draggingRef.current = true;
        movedRef.current = false;
        pausedRef.current = true;
        startXRef.current = e.pageX - stripRef.current.offsetLeft;
        scrollStartRef.current = stripRef.current.scrollLeft;
        stripRef.current.style.cursor = "grabbing";
      }}
      onMouseMove={(e) => {
        if (!draggingRef.current) return;
        const x = e.pageX - stripRef.current.offsetLeft;
        const delta = x - startXRef.current;
        // Below the slop this is still a click, not a drag. Without the
        // threshold a single pixel of hand jitter between mousedown and mouseup
        // marked the gesture as a drag, and onClick below cancelled the
        // navigation -- which is why chips only failed to open *sometimes*.
        if (Math.abs(delta) > DRAG_SLOP) movedRef.current = true;
        if (!movedRef.current) return; // and do not nudge the strip either
        stripRef.current.scrollLeft = scrollStartRef.current - delta;
      }}
      onMouseUp={() => {
        draggingRef.current = false;
        pausedRef.current = false;
        if (stripRef.current) stripRef.current.style.cursor = "grab";
      }}
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        draggingRef.current = false;
        pausedRef.current = false;
        if (stripRef.current) stripRef.current.style.cursor = "grab";
      }}
      onTouchStart={() => {
        pausedRef.current = true;
      }}
      onTouchEnd={() => {
        pausedRef.current = false;
      }}
    >
      {[...CATEGORIES, ...CATEGORIES].map((cat, i) => (
        <Link
          key={`${cat}-${i}`}
          to={`/search?category=${encodeURIComponent(cat)}`}
          style={{ textDecoration: "none", flexShrink: 0 }}
          draggable={false}
          aria-hidden={i >= CATEGORIES.length}
          tabIndex={i >= CATEGORIES.length ? -1 : undefined}
          onClick={(e) => {
            // detail is 0 for a keyboard-triggered click. Only a pointer can
            // have dragged, and movedRef stays set until the next mousedown,
            // so without this a drag would swallow a later Enter on a chip.
            if (e.detail > 0 && movedRef.current) e.preventDefault();
          }}
        >
          <div className="cat-card">
            <img
              src={`/images/categories/${cat.toLowerCase()}.png`}
              alt=""
              draggable={false}
              loading="lazy"
            />
            <span className="cat-card__label">{cat}</span>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default CategoryStrip;
